import { GamePack } from '@/API';
import { createSession } from '@/client/session';
import { PromptExtended } from '@/components/GamePacks/Prompts/PromptList';
import ReloadIcon from '@/components/Icons/ReloadIcon';
import { useAuth } from '@/providers/AuthProvider';
import { listGamePackHost } from '@/utils/gamePacks';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import sandClockImage from '@/assets/images/sand-clock.png';
import ClockIcon from '@/components/Icons/ClockIcon';
import ReactGA from 'react-ga4';
import { bindZoomMeetingToSession } from '@/client/zoom';
import { useZoom } from '@/providers/ZoomProvider';
import { hostSessionPath } from '@/utils/sessionLinks';

// Main component
export default function QuickGame() {
  const [availablePrompts, setAvailablePrompts] = useState<PromptExtended[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptExtended>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isGuide, setIsGuide] = useState<boolean>(false);
  const [playersCount, setPlayersCount] = useState(0);

  const navigate = useNavigate();
  const auth = useAuth();
  const zoom = useZoom();
  const [searchParams] = useSearchParams();

  const tenantId = auth.groups?.find((group) => group.startsWith('tenant:'))?.split(':')[1];

  const estimatedTime = useMemo(() => {
    return isNaN(playersCount) ? 0 : 1 * playersCount;
  }, [playersCount]);

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;
      const response = await listGamePackHost();
      const gamePacks = response.data.gamePackList as GamePack[];

      const prompts = gamePacks.reduce((prompts, gamePack) => {
        if (gamePack.prompts && gamePack.prompts.length > 0) {
          return [
            ...prompts,
            ...gamePack.prompts.map((prompt) => {
              return {
                ...prompt,
                gamePack: gamePack
              };
            })
          ] as PromptExtended[];
        }

        return prompts;
      }, [] as PromptExtended[]);

      setAvailablePrompts(prompts);
    };

    if (searchParams.has('guide')) {
      setIsGuide(true);
    }

    fetchData();
  }, [tenantId]);

  useEffect(() => {
    if (!selectedPrompt) {
      getNewPrompt();
    }
  }, [selectedPrompt, availablePrompts]);

  const getNewPrompt = () => {
    setSelectedPrompt(availablePrompts[Math.floor(Math.random() * availablePrompts.length)]);
  };

  const launchGame = async () => {
    if (!selectedPrompt) return;

    setLoading(true);
    ReactGA.event({
      category: 'Quick Game',
      action: 'Clicked Button',
      label: 'Start Quick Game'
    });
    try {
      const result = await createSession({
        promptIds: [selectedPrompt.id]
      });

      if (!result?.id) return;

      // Inside a Zoom meeting, bind the game to the meeting so other participants
      // who open the app join this game directly. Failure is non-fatal: the QR code
      // and shareable link still work.
      if (zoom.embedded && zoom.inMeeting) {
        await bindZoomMeetingToSession(result.id);
      }

      navigate(hostSessionPath(result.id));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isGuide && (
        <div className="mx-32 relative top-0 w-auto">
          <div className={clsx('absolute h-4 bg-primary-500 -top-60', 'w-full')}></div>
        </div>
      )}
      <div className="py-32 px-40 bg-white z-100 rounded-xs relative w-608 m-auto">
        <h2 className="text-h2 text-center mb-12">Quick Game</h2>
        <h3 className="text-h3 text-center mb-24">Here is your randomly selected prompt!</h3>
        <div className="py-12 border-y border-neutral-200 flex flex-col gap-12">
          <p className="text-body text-body-strong">Selected Prompts</p>
          {selectedPrompt && (
            <div className="w-full py-8 px-12 border-1 border-primary-500 bg-primary-100 rounded-xs h-76 justify-center flex flex-col">
              <p className="text-[16px] leading-20 mb-4 font-semibold">{selectedPrompt.text}</p>
              <p className="text-body-xs text-neutral-400 uppercase tracking-[0.24px] font-semibold">
                {selectedPrompt.gamePack?.name}
              </p>
            </div>
          )}

          <button className="btn btn-neutral" onClick={getNewPrompt}>
            <ReloadIcon className="w-24 h-24 mr-8" />
            Give Me a New Prompt
          </button>
        </div>
        <div className="py-12 border-b border-neutral-200 flex flex-col gap-12">
          <div className="p-12 rounded-xs bg-neutral-100 flex justify-start items-start gap-12">
            <img src={sandClockImage} className="w-20" />
            <div>
              <p className="text-body text-body-sm-strong">Not sure how long Scribl takes?</p>
              <p className="text-body text-body-sm text-neutral-400">
                Add your player count below for a rough estimate (min. 2 players)
              </p>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-8">
              <input
                type="integer"
                name="players-qty"
                placeholder="Players Qty"
                value={isNaN(playersCount) || playersCount === 0 ? '' : playersCount}
                onChange={(e) => {
                  e.preventDefault();
                  const count = parseInt(e.target.value);
                  if (count > 999) return;
                  setPlayersCount(parseInt(e.target.value));
                }}
                className="rounded-3xs text-[14px] leading-24 border-1 border-neutral-200 focus:border-neutral-500 px-12 py-4 w-95 text-neutral-500 placeholder:text-neutral-200 outline-none"
              />
              <p className="text-body text-body-sm-strong">x</p>
              <p className="text-body text-body-sm-strong">1 prompt</p>
            </div>
            <div className="flex justify-start items-center gap-4">
              <ClockIcon className="w-16 h-16" />
              <p className="text-body text-body-sm">Estimated Game Time:</p>
              <p className="text-body text-body-sm-strong">{estimatedTime}</p>
              <p className="text-body text-body-sm-strong">
                {estimatedTime === 1 ? 'min' : 'mins'}
              </p>
            </div>
          </div>
        </div>
        <h3 className="text-h3 text-center mt-20">You’re all set!</h3>
        <p className="text-body-sm text-neutral-400 mb-24 text-center">
          Just hit Start when everyone’s ready.
        </p>
        <button className="btn btn-primary w-full" disabled={loading} onClick={launchGame}>
          Start Game
        </button>
      </div>
      {isGuide ? (
        <p className="text-body-sm text-center text-neutral-400 relative mt-16">
          Not ready to play yet?{' '}
          <Link className="link link-sm" to="/dashboard/host">
            Skip and Explore
          </Link>
        </p>
      ) : (
        <p className="text-body-sm text-center text-neutral-400 relative mt-16">
          Want to customize your game?{' '}
          <Link className="link link-sm" to="/dashboard/host">
            Explore All Prompts
          </Link>
        </p>
      )}
    </>
  );
}
